import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { isSafeInternalPath } from '@/lib/common/isSafeInternalPath'

// Bumped when the persisted shape changes; older payloads are dropped via
// `migrate` so we don't try to read `string` as `{ url, setAt }`.
const PERSIST_VERSION = 1

// Window during which a pending redirect remains honored after it was set.
// Auth flows complete within seconds; anything past this is almost certainly
// a stale artifact from an abandoned past session and should not be replayed.
const REDIRECT_TTL_MS = 10 * 60 * 1000

interface StoredRedirect {
  url: string
  setAt: number
}

interface AuthState {
  pendingRedirect: StoredRedirect | null
  setPendingRedirect: (url: string | null) => void
  consumeRedirect: () => string | null
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      pendingRedirect: null,

      // Stores any string as-is; safety + TTL are enforced at consumeRedirect.
      setPendingRedirect: (url: string | null) => {
        set({
          pendingRedirect: url ? { url, setAt: Date.now() } : null,
        })
      },

      // Clear FIRST; an expired or unsafe value must not linger across reads.
      consumeRedirect: () => {
        const stored = get().pendingRedirect
        set({ pendingRedirect: null })
        if (!stored || typeof stored.url !== 'string') return null
        if (Date.now() - stored.setAt > REDIRECT_TTL_MS) return null
        if (!isSafeInternalPath(stored.url)) return null
        return stored.url
      },
    }),
    {
      name: 'auth-store',
      version: PERSIST_VERSION,
      // Only persist the redirect, not temporary UI state
      partialize: (state) => ({ pendingRedirect: state.pendingRedirect }),
      // v0 stored the URL as a bare string; drop those so we don't read a
      // string as `{ url, setAt }`. Future versions slot in as new arms
      // before the pass-through (currently v1 → current, no shape change).
      migrate: (state, version) =>
        version === 0
          ? { pendingRedirect: null }
          : (state as { pendingRedirect: StoredRedirect | null }),
    }
  )
)
